import { gsap } from 'gsap';
import { blockColorMap } from '../../defs/block';
import { CollideEvent, CollideId } from '../../defs/collide';
import {
  CurtainBlockEntity,
  despawnBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { BreakEffect } from '../effects/BreakEffect';
import { CollideHandler, ICollision } from './ICollision';
import { app } from '../../app';
import { mapGetPan } from '../../util/mapTools';

/*
  Curtain block collision resolver
*/
export class CurtainCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: CurtainBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    goal: this._resolveGoal,
  };

  constructor(scene: GameScene, entity: CurtainBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveGoal() {
    const { c } = this._entity;
    const component = c.blockCurtain;
    const position = c.position.mapPosition;
    const color = blockColorMap[c.blockCurtain.color].base;

    // Already broken.
    if (component.counter <= 0) {
      return false;
    }

    // Play sound. Reuse the poke chain sound.
    app.sound.play('puzzle-door-poke-chain', { pan: mapGetPan(position) });

    // execute break effect on gem
    await new BreakEffect(this._scene, {
      position,
      size: this._entity.c.block.props,
      color,
    }).execute();
    this._scene?.events.publish({ id: 'poke' });

    // Decrement counter.
    component.counter--;

    // If counter is 0.
    if (component.counter <= 0) {
      // Stretch curtain on Y axis.
      await gsap.to(this._entity.view, {
        scaleY: 1.05,
        duration: 0.2,
        ease: 'power2.in',
      });

      // Play sound. This sounds like a curtain being pulled.
      app.sound.play('puzzle-hat-destroy', { pan: mapGetPan(position) });

      // Shrink curtain on Y axis.
      await gsap.to(this._entity.view, {
        scaleY: 0,
        duration: 0.25,
        ease: 'power2.in',
      });

      // Play sound. This sounds like the curtain hitting the frame.
      app.sound.play('puzzle-candle-destroy', { pan: mapGetPan(position) });

      // despawn entity
      despawnBlockEntity(this._scene, this._entity);
    }

    return true;
  }
}
